/**
 * Alta rápida de un cliente en el maestro (facturas emitidas).
 * Espejo simplificado de QuickSupplierDialog: cuenta 430, cuenta de venta 700,
 * código postal (modelo 347) y tipo de operación.
 */
import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { upsertCustomer, type CustomerMasterRecord } from "@/lib/customerMaster";

interface QuickCustomerDialogProps {
  open: boolean;
  companyId: string | null;
  initial: Partial<CustomerMasterRecord>;
  onClose: () => void;
  onSaved: (record: CustomerMasterRecord) => void;
}

const QuickCustomerDialog = ({ open, companyId, initial, onClose, onSaved }: QuickCustomerDialogProps) => {
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<CustomerMasterRecord>({
    taxId: "",
    accountCode: "",
    salesAccount: "",
    description: "",
    operationType: "interior",
    postalCode: "",
  });

  useEffect(() => {
    if (!open) return;
    setForm({
      taxId: initial.taxId || "",
      accountCode: initial.accountCode || "",
      salesAccount: initial.salesAccount || "",
      description: initial.description || "",
      operationType: initial.operationType || "interior",
      postalCode: initial.postalCode || "",
    });
  }, [open, initial.taxId, initial.accountCode, initial.salesAccount, initial.description, initial.operationType, initial.postalCode]);

  const set = (patch: Partial<CustomerMasterRecord>) => setForm((f) => ({ ...f, ...patch }));

  const handleSave = async () => {
    if (!companyId) return;
    if (!form.taxId.trim() || !form.description.trim() || !form.accountCode.trim()) {
      toast({ title: "Faltan datos", description: "NIF, nombre y cuenta de cliente son obligatorios.", variant: "destructive" });
      return;
    }
    setSaving(true);
    const { error } = await upsertCustomer(companyId, form);
    setSaving(false);
    if (error) {
      toast({ title: "No se pudo guardar", description: error, variant: "destructive" });
      return;
    }
    toast({ title: "Cliente guardado en el maestro" });
    onSaved(form);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Alta rápida de cliente</DialogTitle>
        </DialogHeader>
        <div className="grid gap-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">NIF</Label>
              <Input value={form.taxId} onChange={(e) => set({ taxId: e.target.value.toUpperCase() })} autoComplete="off" />
            </div>
            <div>
              <Label className="text-xs">Código postal</Label>
              <Input
                value={form.postalCode || ""}
                onChange={(e) => set({ postalCode: e.target.value.replace(/\D/g, "").slice(0, 5) })}
                inputMode="numeric"
                autoComplete="off"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs">Nombre del cliente</Label>
            <Input value={form.description} onChange={(e) => set({ description: e.target.value })} autoComplete="off" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Cuenta de cliente (430)</Label>
              <Input value={form.accountCode} onChange={(e) => set({ accountCode: e.target.value })} autoComplete="off" />
            </div>
            <div>
              <Label className="text-xs">Cuenta de venta (700)</Label>
              <Input value={form.salesAccount} onChange={(e) => set({ salesAccount: e.target.value })} autoComplete="off" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleSave} disabled={saving} className="gap-2">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            Guardar cliente
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default QuickCustomerDialog;
